import React from 'react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import PlanPriceDisplay from './PlanPriceDisplay';
import { cycleSuffix } from '../../utils/planPricing';

/**
 * SaaS-style plan card for school admin upgrade/compare page.
 */
export default function PlanPricingCard({
  plan,
  cycle,
  isCurrent = false,
  onSelect,
  onRenew,
  actionLabel = 'Select Plan',
  showFeatures = true,
}) {
  if (!plan) return null;

  const limits = plan.features?.limits ?? {};
  const enabledModules = Object.entries(plan.features?.modules ?? {})
    .filter(([, v]) => v)
    .slice(0, 4)
    .map(([k]) => k.replace(/_/g, ' '));

  return (
    <Card className={`flex flex-col h-full ${plan.is_popular ? 'border-blue-500 shadow-md ring-1 ring-blue-500/20' : ''}`}>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-lg">{plan.name}</CardTitle>
          {isCurrent && (
            <Badge variant="secondary" className="shrink-0">Current Plan</Badge>
          )}
        </div>
        {plan.is_popular && !isCurrent && (
          <span className="text-xs font-medium text-blue-600">Most popular</span>
        )}
        <p className="text-xs text-muted-foreground">
          From {cycle.replace(/_/g, ' ')} billing{cycleSuffix(cycle)}
        </p>
      </CardHeader>
      <CardContent className="flex flex-col flex-1 space-y-4">
        <PlanPriceDisplay plan={plan} cycle={cycle} size="md" showCycleSuffix={false} />

        {plan.description && (
          <p className="text-sm text-muted-foreground line-clamp-2">{plan.description}</p>
        )}

        {showFeatures && (
          <ul className="text-xs text-muted-foreground space-y-1 flex-1">
            <li>Up to {limits.max_students ?? '—'} students</li>
            {enabledModules.length > 0 && (
              <li className="capitalize">{enabledModules.join(' · ')}</li>
            )}
            {plan.trial_days > 0 && <li>{plan.trial_days}-day free trial</li>}
          </ul>
        )}

        <div className="space-y-2 pt-2">
          {isCurrent ? (
            <>
              <Button className="w-full" variant="secondary" disabled>Current Plan</Button>
              {onRenew && (
                <Button className="w-full" variant="outline" onClick={() => onRenew(plan)}>
                  Renew
                </Button>
              )}
            </>
          ) : (
            <Button className="w-full bg-blue-600" onClick={() => onSelect?.(plan)}>
              {actionLabel}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
